import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Clock } from 'lucide-react';

import apiClient from '@/lib/apiClient';

/**
 * Página de retorno de pago.
 *
 * Las pasarelas (Payku, Flow, Stripe) redirigen aquí al pagador tras
 * completar o cancelar el pago.
 *
 * URL: /payments/return?gateway=flow&document_id=xxx&token=xxx
 *
 * El estado real del pago lo determina el webhook (asíncrono). Esta página
 * consulta /payments/return/info/ con (token, document_id) para mostrar los
 * datos del documento + estado del PaymentLink. Si el webhook todavía no
 * llegó cuando el usuario aterriza acá, mostramos "Verificando..." y
 * refrescamos cada 3s hasta paid/cancelled o timeout.
 */

interface PaymentInfo {
  gateway: string;
  gateway_display: string;
  status: 'active' | 'paid' | 'expired' | 'cancelled';
  paid_at: string | null;
  amount: string;
  currency: string;
  document: {
    document_type: string;
    document_type_display: string;
    folio: number | null;
    issue_date: string | null;
    total: string;
    customer_name: string;
  };
}

function fmtMoney(value: string, currency = 'CLP') {
  const n = parseFloat(value);
  if (isNaN(n)) return '—';
  return n.toLocaleString('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 });
}

export default function PaymentReturnPage() {
  const [params] = useSearchParams();
  const gateway = params.get('gateway') || '';
  const documentId = params.get('document_id') || '';
  const token = params.get('token') || '';
  const messageError = params.get('message_error');

  const [info, setInfo] = useState<PaymentInfo | null>(null);
  const [error, setError] = useState<string | null>(messageError);
  const [polls, setPolls] = useState(0);

  useEffect(() => {
    if (messageError || !token || !documentId) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20; // ~60s

    const fetchInfo = async () => {
      attempts += 1;
      try {
        const { data } = await apiClient.get<PaymentInfo>('/payments/return/info/', {
          params: { token, document_id: documentId },
        });
        if (cancelled) return;
        setInfo(data);
        setPolls(attempts);
        // Si todavía está active, el webhook puede no haber llegado. Reintentamos.
        if (data.status === 'active' && attempts < maxAttempts) {
          setTimeout(fetchInfo, 3000);
        }
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.error?.message || 'No se pudo cargar el detalle del pago.');
      }
    };

    fetchInfo();
    return () => { cancelled = true; };
  }, [token, documentId, messageError]);

  const isPaid = info?.status === 'paid';
  const isPendingConfirm = info?.status === 'active';
  const isRejected = info?.status === 'cancelled';
  const isExpired = info?.status === 'expired';
  const isCancelled = isRejected || isExpired;

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-muted p-4">
      <div className="bg-surface rounded-xl shadow-lg p-8 max-w-lg w-full">
        {/* Loading inicial */}
        {!info && !error && (
          <div className="text-center">
            <Loader2 className="w-14 h-14 text-primary animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-content">Procesando pago...</h1>
            <p className="text-content-muted mt-2">Verificando la transacción.</p>
          </div>
        )}

        {/* Error explícito de pasarela */}
        {error && (
          <div className="text-center">
            <XCircle className="w-14 h-14 text-danger-solid mx-auto mb-4" />
            <h1 className="text-xl font-bold text-content">Pago no completado</h1>
            <p className="text-content-muted mt-2">
              {messageError === 'expired'
                ? 'La transacción ha expirado. Por favor intenta nuevamente.'
                : error}
            </p>
          </div>
        )}

        {/* Info del pago */}
        {info && !error && (
          <>
            <div className="text-center mb-6">
              {isPaid && <CheckCircle className="w-14 h-14 text-success-solid mx-auto mb-4" />}
              {isPendingConfirm && <Clock className="w-14 h-14 text-warning-solid mx-auto mb-4 animate-pulse" />}
              {isCancelled && <XCircle className="w-14 h-14 text-danger-solid mx-auto mb-4" />}

              <h1 className="text-xl font-bold text-content">
                {isPaid && 'Pago confirmado'}
                {isPendingConfirm && 'Pago en proceso de confirmación'}
                {isRejected && 'Pago rechazado'}
                {isExpired && 'Pago expirado'}
              </h1>
              <p className="text-content-muted mt-2 text-sm">
                {isPaid && `Gracias por tu pago vía ${info.gateway_display}.`}
                {isPendingConfirm && (
                  <>
                    Estamos esperando la confirmación de {info.gateway_display}.
                    Esta página se actualiza sola. {polls > 1 && `(intento ${polls})`}
                  </>
                )}
                {isRejected && (
                  <>
                    {info.gateway_display} no aprobó el cobro. Por favor intenta
                    nuevamente con otro medio de pago o contacta a la empresa.
                  </>
                )}
                {isExpired && 'El link de pago expiró. Solicita uno nuevo.'}
              </p>
            </div>

            <div className="border-t border-divider pt-4 space-y-2 text-sm">
              <Row label="Documento">
                {info.document.document_type_display}
                {info.document.folio ? ` N° ${info.document.folio}` : ''}
              </Row>
              {info.document.customer_name && (
                <Row label="A nombre de">{info.document.customer_name}</Row>
              )}
              <Row label="Monto" strong>{fmtMoney(info.amount, info.currency)}</Row>
              {info.paid_at && (
                <Row label="Pagado el">
                  {new Date(info.paid_at).toLocaleString('es-CL')}
                </Row>
              )}
              <Row label="Vía">{info.gateway_display}</Row>
            </div>

            {isPaid && (
              <p className="text-xs text-content-muted text-center mt-6">
                Puedes cerrar esta ventana.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, children, strong = false }: {
  label: string; children: React.ReactNode; strong?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-content-muted">{label}</span>
      <span className={`text-content ${strong ? 'font-semibold tabular-nums' : ''}`}>
        {children}
      </span>
    </div>
  );
}
